
import React from 'react';
import { AppNotification } from '../types';
import { ICONS } from '../constants';
import { downloadCalendarInvite } from '../utils/calendar';

interface NotificationCentreProps {
  notifications: AppNotification[];
  onMarkAsRead: (id: string) => void;
  onClearAll: () => void;
}

const NotificationCentre: React.FC<NotificationCentreProps> = ({ 
  notifications, 
  onMarkAsRead,
  onClearAll
}) => {
  return (
    <div className="max-w-3xl mx-auto space-y-10">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-900 tracking-tight">Personnel Alerts</h2>
        {notifications.length > 0 && (
          <button 
            onClick={onClearAll}
            className="text-[10px] font-black text-rose-600 uppercase tracking-widest hover:text-rose-800 transition-colors"
          >
            Archive All
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-slate-50 p-20 rounded-[2rem] border-2 border-dashed border-slate-200 text-center">
          <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-slate-100">
            <ICONS.Bell className="w-10 h-10 text-slate-100" />
          </div>
          <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No Recent Activity</p>
        </div>
      ) : (
        <div className="space-y-6">
          {notifications.map((n) => (
            <div 
              key={n.id} 
              className={`p-8 rounded-2xl border-2 transition-all group ${
                n.isRead ? 'bg-white border-slate-100' : 'bg-red-50/30 border-accent-red shadow-lg shadow-red-100/20'
              }`}
            >
              <div className="flex items-start justify-between gap-6">
                <div className="flex gap-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all ${
                    n.isRead ? 'bg-slate-50 text-slate-400' : 'bg-accent-red text-white shadow-lg'
                  }`}>
                    {n.type === 'ASSIGNMENT' ? <ICONS.Check className="w-7 h-7" /> : <ICONS.Bell className="w-7 h-7" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h4 className="font-black text-slate-900 text-lg leading-tight tracking-tight">{n.title}</h4>
                      {!n.isRead && <div className="w-2.5 h-2.5 rounded-full bg-accent-red animate-pulse" />}
                    </div>
                    <p className="text-slate-500 font-medium text-sm mt-1">{n.message}</p>
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] mt-3">
                      {new Date(n.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => downloadCalendarInvite(n.date, n.spaceLabel)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-900 hover:border-accent-red hover:text-accent-red transition-all shadow-sm"
                  >
                    <ICONS.Calendar className="w-4 h-4" /> Save
                  </button>
                  {!n.isRead && (
                    <button 
                      onClick={() => onMarkAsRead(n.id)}
                      className="text-[10px] font-black text-slate-400 hover:text-accent-red uppercase tracking-widest text-center"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationCentre;
